import { useCallback, useEffect, useState } from 'react';
import type { BillingConfig, CartItem } from '@cartiq/shared';
import { SUPPORTED_COUNTRIES, computeBilling, isValidEmail } from '@cartiq/shared';
import { API_URL } from '../config';
import BillingSummary from './BillingSummary';

interface Props {
  slug: string;
  token: string;
}

interface StoreBrand {
  name?: string;
  logoUrl?: string | null;
  brandColors?: { primary?: string; accent?: string } | null;
  billingConfig?: BillingConfig | null;
}

interface CheckoutData {
  cart: CartItem[];
  currency: string;
  total: number;
  store?: StoreBrand | null;
}

type Phase = 'loading' | 'ready' | 'invalid' | 'paying';

function formatPrice(price: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}

const itemKey = (i: CartItem) => `${i.productId}::${i.variantId ?? ''}`;

export default function CheckoutPage({ slug, token }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [store, setStore] = useState<StoreBrand | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [country, setCountry] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [deliveryLocation, setDeliveryLocation] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [deliveryInstructions, setDeliveryInstructions] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const base = `${API_URL}/api/stores/${encodeURIComponent(slug)}/checkout/${encodeURIComponent(token)}`;

  // Load the cart behind the handoff token.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(base);
        const body = await res.json();
        if (cancelled) return;
        if (!res.ok || !body?.success) {
          setLoadError(body?.error?.message ?? 'This checkout link is invalid or has expired.');
          setPhase('invalid');
          return;
        }
        const data = body.data as CheckoutData;
        setCart(data.cart ?? []);
        setStore(data.store ?? null);
        setPhase('ready');
      } catch {
        if (cancelled) return;
        setLoadError('We could not reach the store. Please try again.');
        setPhase('invalid');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base]);

  // Apply store brand colors when available.
  useEffect(() => {
    const c = store?.brandColors;
    if (c?.accent) document.documentElement.style.setProperty('--brand-accent', c.accent);
    if (c?.primary) document.documentElement.style.setProperty('--brand-primary', c.primary);
  }, [store]);

  const setQuantity = useCallback((key: string, quantity: number) => {
    setCart((prev) =>
      prev
        .map((i) => (itemKey(i) === key ? { ...i, quantity } : i))
        .filter((i) => i.quantity > 0),
    );
  }, []);

  const currency = cart[0]?.currency ?? 'USD';
  const count = cart.reduce((n, i) => n + i.quantity, 0);
  const deliveryLocations = store?.billingConfig?.delivery?.enabled
    ? store.billingConfig.delivery.locations ?? []
    : [];
  const requiresShipping = cart.some(
    (item) => (item.fulfillmentType ?? 'physical') === 'physical',
  );
  const effectiveSlug = deliveryLocation || undefined;
  const breakdown = computeBilling(cart, store?.billingConfig ?? null, {
    deliveryLocationSlug: effectiveSlug,
  });
  const total = breakdown.total;
  const blocked = Boolean(breakdown.belowMinimum || breakdown.issues?.length);

  const pay = useCallback(async () => {
    if (!country) {
      setFormError('Please select your country.');
      return;
    }
    if (requiresShipping && deliveryLocations.length > 0 && !deliveryLocation) {
      setFormError('Please choose a delivery destination.');
      return;
    }
    if (
      requiresShipping &&
      (!name.trim() || !phone.trim() || !addressLine1.trim() || !city.trim())
    ) {
      setFormError('Please enter the recipient name, phone, street address, and city.');
      return;
    }
    if (!isValidEmail(email.trim())) {
      setFormError('Please enter a valid email address.');
      return;
    }
    if (cart.length === 0) {
      setFormError('Your cart is empty.');
      return;
    }
    if (breakdown.issues?.length) {
      setFormError(breakdown.issues[0].message);
      return;
    }
    if (breakdown.belowMinimum) {
      setFormError(
        `Orders start at ${formatPrice(breakdown.belowMinimum.minimum, breakdown.currency)}.`,
      );
      return;
    }
    setFormError(null);
    setPhase('paying');
    try {
      const res = await fetch(base, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerEmail: email.trim(),
          customerName: name.trim() || undefined,
          customerPhone: phone.trim() || undefined,
          customerCountry: country,
          deliveryLocationSlug: effectiveSlug,
          fulfillmentMethod: requiresShipping ? 'shipping' : undefined,
          deliveryAddress: requiresShipping
            ? {
                line1: addressLine1.trim(),
                line2: addressLine2.trim() || undefined,
                city: city.trim(),
                region: region.trim() || undefined,
                postalCode: postalCode.trim() || undefined,
                country,
              }
            : undefined,
          deliveryInstructions: deliveryInstructions.trim() || undefined,
          items: cart.map((i) => ({
            productId: i.productId,
            variantId: i.variantId,
            quantity: i.quantity,
          })),
        }),
      });
      const body = await res.json();
      if (!res.ok || !body?.success) {
        setFormError(body?.error?.message ?? 'Could not start checkout. Please try again.');
        setPhase('ready');
        return;
      }
      // Hand off to the hosted payment page.
      window.location.href = body.data.url as string;
    } catch {
      setFormError('Could not start checkout. Please try again.');
      setPhase('ready');
    }
  }, [
    addressLine1,
    addressLine2,
    base,
    breakdown,
    cart,
    city,
    country,
    deliveryInstructions,
    deliveryLocation,
    deliveryLocations.length,
    effectiveSlug,
    email,
    name,
    phone,
    postalCode,
    region,
    requiresShipping,
  ]);

  const inputClass =
    'w-full rounded-sm border border-border bg-white px-3 py-2.5 text-sm text-primary outline-none transition-colors focus:border-[var(--brand-accent)]';

  return (
    <div className="min-h-[100dvh] w-full bg-surface">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-[480px] flex-col bg-white shadow-card">
        <header className="flex items-center gap-3 border-b border-border px-5 py-4">
          {store?.logoUrl ? (
            <img src={store.logoUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
          ) : null}
          <div>
            <p className="text-sm font-bold text-primary">{store?.name ?? 'Checkout'}</p>
            <p className="text-[11px] text-mid">Review your order & pay securely</p>
          </div>
        </header>

        {phase === 'loading' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-3">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-[var(--brand-accent)]" />
            <p className="text-xs text-mid">Loading your cart…</p>
          </div>
        )}

        {phase === 'invalid' && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
            <div className="text-2xl">🔒</div>
            <p className="text-sm font-semibold text-primary">Checkout unavailable</p>
            <p className="max-w-xs text-xs text-mid">{loadError}</p>
            <p className="mt-2 max-w-xs text-[11px] text-mid">
              Head back to your chat and ask for a fresh checkout link.
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'paying') && cart.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
            <div className="text-2xl">🛒</div>
            <p className="text-sm font-semibold text-primary">Your cart is empty</p>
            <p className="max-w-xs text-xs text-mid">
              Add an item in your chat, then come back to check out.
            </p>
          </div>
        )}

        {(phase === 'ready' || phase === 'paying') && cart.length > 0 && (
          <div className="flex flex-1 flex-col">
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-mid">
                {count} item{count === 1 ? '' : 's'}
              </p>
              <div className="divide-y divide-border/60">
                {cart.map((item) => {
                  const key = itemKey(item);
                  return (
                    <div key={key} className="flex items-center justify-between gap-2 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-primary">
                          {item.productName}
                        </p>
                        <span className="text-xs font-semibold text-primary">
                          {formatPrice(item.price * item.quantity, item.currency)}
                        </span>
                      </div>
                      <div className="flex items-center rounded-sm border border-border">
                        <button
                          onClick={() => setQuantity(key, item.quantity - 1)}
                          aria-label={`Decrease ${item.productName} quantity`}
                          className="flex h-9 w-9 items-center justify-center text-mid transition-colors hover:bg-surface active:scale-90"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                            <path d="M5 12h14" />
                          </svg>
                        </button>
                        <span
                          className="min-w-6 text-center text-sm font-semibold text-primary"
                          aria-label={`Quantity ${item.quantity}`}
                        >
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => setQuantity(key, item.quantity + 1)}
                          aria-label={`Increase ${item.productName} quantity`}
                          className="flex h-9 w-9 items-center justify-center text-mid transition-colors hover:bg-surface active:scale-90"
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex flex-col gap-2.5">
                {requiresShipping && deliveryLocations.length > 0 && (
                  <select
                    value={deliveryLocation}
                    onChange={(e) => setDeliveryLocation(e.target.value)}
                    required
                    className={inputClass}
                    aria-label="Delivery destination"
                  >
                    <option value="" disabled>
                      Where should we deliver?
                    </option>
                    {deliveryLocations.map((l) => (
                      <option key={l.slug} value={l.slug}>
                        {l.location}
                      </option>
                    ))}
                  </select>
                )}
                <select
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  required
                  className={inputClass}
                  aria-label="Country"
                >
                  <option value="" disabled>
                    Select your country…
                  </option>
                  {SUPPORTED_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Email address"
                  autoComplete="email"
                  className={inputClass}
                  aria-label="Email address"
                />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={requiresShipping ? 'Recipient full name' : 'Full name (optional)'}
                  autoComplete="name"
                  className={inputClass}
                  aria-label="Full name"
                />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={requiresShipping ? 'Recipient phone number' : 'Phone (optional)'}
                  autoComplete="tel"
                  className={inputClass}
                  aria-label="Phone number"
                />
                {requiresShipping && (
                  <>
                    <input
                      value={addressLine1}
                      onChange={(e) => setAddressLine1(e.target.value)}
                      placeholder="Street address"
                      autoComplete="address-line1"
                      className={inputClass}
                      aria-label="Street address"
                    />
                    <input
                      value={addressLine2}
                      onChange={(e) => setAddressLine2(e.target.value)}
                      placeholder="Apartment, suite, etc. (optional)"
                      autoComplete="address-line2"
                      className={inputClass}
                      aria-label="Address line 2"
                    />
                    <div className="grid grid-cols-2 gap-2.5">
                      <input
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        placeholder="City"
                        autoComplete="address-level2"
                        className={inputClass}
                        aria-label="City"
                      />
                      <input
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        placeholder="State / region"
                        autoComplete="address-level1"
                        className={inputClass}
                        aria-label="State or region"
                      />
                    </div>
                    <input
                      value={postalCode}
                      onChange={(e) => setPostalCode(e.target.value)}
                      placeholder="Postal code (optional)"
                      autoComplete="postal-code"
                      className={inputClass}
                      aria-label="Postal code"
                    />
                    <textarea
                      value={deliveryInstructions}
                      onChange={(e) => setDeliveryInstructions(e.target.value)}
                      placeholder="Delivery instructions (optional)"
                      className={inputClass}
                      aria-label="Delivery instructions"
                      rows={2}
                    />
                  </>
                )}
                {formError && <p className="text-xs font-medium text-error">{formError}</p>}
              </div>
            </div>

            <div className="border-t border-border p-4">
              <div className="mb-3">
                <BillingSummary
                  cart={cart}
                  billing={store?.billingConfig}
                  deliveryLocationSlug={effectiveSlug}
                />
              </div>
              <button
                onClick={pay}
                disabled={phase === 'paying' || blocked}
                className="w-full rounded-sm px-4 py-3 text-sm font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-60"
                style={{ backgroundColor: 'var(--brand-accent)' }}
              >
                {phase === 'paying' ? 'Starting secure checkout…' : `Pay · ${formatPrice(total, currency)}`}
              </button>
              <p className="mt-2 text-center text-[11px] text-mid">
                Your country determines the secure payment method.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
